<template>
  <div class="transaction-dashboard">
    <div class="header">
      <h2>真实交易数据统计看板</h2>
    </div>

    <el-card class="summary-card" style="margin-bottom: 20px;">
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="stat-box">
            <div class="stat-title">总营收金额</div>
            <div class="stat-value">¥ {{ formatAmount(totalRevenue) }}</div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-box">
            <div class="stat-title">成交订单数</div>
            <div class="stat-value">{{ totalOrders }} 笔</div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-box">
            <div class="stat-title">活跃消费企业数</div>
            <div class="stat-value">{{ activeEnterprisesCount }} 家</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="12">
        <el-card>
          <div slot="header">服务类型营收占比</div>
          <div ref="pieChart" style="height: 350px; width: 100%; display: block;"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card>
          <div slot="header">企业消费排行榜 (Top 10)</div>
          <div ref="barChart" style="height: 350px; width: 100%; display: block;"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-card>
      <div slot="header">成功交易流水明细</div>
      <el-table :data="paginatedOrders" v-loading="loading" border style="width: 100%">
        <el-table-column prop="id" label="订单ID" width="80"></el-table-column>
        <el-table-column prop="enterpriseName" label="购买企业" min-width="150"></el-table-column>
        <el-table-column prop="serviceName" label="服务名称" min-width="150"></el-table-column>
        <el-table-column prop="customerName" label="下单人姓名" width="120"></el-table-column>
        <el-table-column prop="amount" label="交易金额" width="120" align="right">
          <template slot-scope="scope">
            <span class="positive-amount">¥{{ formatAmount(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="成交时间" width="160">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>
      </el-table>
      
      <el-pagination 
        @size-change="handleSizeChange" 
        @current-change="handleCurrentChange" 
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]" 
        :page-size="pagination.size" 
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total" 
        style="margin-top: 20px; text-align: right">
      </el-pagination>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'TransactionDashboard',
  data() {
    return {
      allOrders: [],
      paginatedOrders: [],
      loading: false,
      pieChartInstance: null,
      barChartInstance: null,
      
      totalRevenue: 0,
      totalOrders: 0,
      activeEnterprisesCount: 0,

      pagination: {
        page: 1,
        size: 10,
        total: 0
      }
    }
  },
  mounted() {
    this.loadRealTransactions()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.pieChartInstance) this.pieChartInstance.dispose()
    if (this.barChartInstance) this.barChartInstance.dispose()
  },
  methods: {
    handleResize() {
      if (this.pieChartInstance) this.pieChartInstance.resize()
      if (this.barChartInstance) this.barChartInstance.resize()
    },
    
    async loadRealTransactions() {
      try {
        this.loading = true
        // 查询所有已支付（确收）的服务订单
        const response = await this.$http.get('/service-orders', { params: { status: 'PAID' } })
        
        if (response.success) {
          this.allOrders = response.data || []
          this.pagination.total = this.allOrders.length
          this.updatePagination()
          // 等待 DOM 完全渲染后再初始化图表，避免容器尺寸为 0
          this.$nextTick(() => {
            this.calculateMetricsAndRenderCharts()
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载交易记录失败:', error)
        this.$message.error('加载真实交易失败')
      } finally {
        this.loading = false
      }
    },

    updatePagination() {
      const start = (this.pagination.page - 1) * this.pagination.size
      const end = this.pagination.page * this.pagination.size
      this.paginatedOrders = this.allOrders.slice(start, end)
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.updatePagination()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.updatePagination()
    },
    
    calculateMetricsAndRenderCharts() {
      let revenue = 0
      const enterpriseMap = {}
      const serviceMap = {}

      this.allOrders.forEach(order => {
        const amt = Number(order.amount) || 0
        revenue += amt
        
        // 汇总企业金额
        const eName = order.enterpriseName || '未知企业'
        if (!enterpriseMap[eName]) enterpriseMap[eName] = 0
        enterpriseMap[eName] += amt

        // 汇总服务类型金额
        const sName = order.serviceName || '其他服务'
        if (!serviceMap[sName]) serviceMap[sName] = 0
        serviceMap[sName] += amt
      })

      this.totalRevenue = revenue
      this.totalOrders = this.allOrders.length
      this.activeEnterprisesCount = Object.keys(enterpriseMap).length

      // 渲染图表
      this.renderPieChart(serviceMap)
      this.renderBarChart(enterpriseMap)
    },

    renderPieChart(serviceMap) {
      if (!this.$refs.pieChart) return
      // 销毁旧实例重建，避免尺寸残留
      if (this.pieChartInstance) {
        this.pieChartInstance.dispose()
      }
      this.pieChartInstance = echarts.init(this.$refs.pieChart)

      const data = Object.keys(serviceMap).map(key => ({
        name: key,
        value: serviceMap[key]
      }))

      if (data.length === 0) return

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: ¥{c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'center'
        },
        series: [
          {
            name: '服务营收',
            type: 'pie',
            radius: ['35%', '60%'],
            center: ['60%', '50%'],
            data: data,
            label: {
              formatter: '{b}\n¥{c}'
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      this.pieChartInstance.setOption(option)
    },

    renderBarChart(enterpriseMap) {
      if (!this.$refs.barChart) return
      // 销毁旧实例重建
      if (this.barChartInstance) {
        this.barChartInstance.dispose()
      }
      this.barChartInstance = echarts.init(this.$refs.barChart)

      // 排序并取 Top 10
      const sortedE = Object.keys(enterpriseMap)
        .map(k => ({ name: k, value: enterpriseMap[k] }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)

      if (sortedE.length === 0) return

      // 翻转以适应横向柱状图（底向上）
      sortedE.reverse()

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            return `${params[0].name}<br/>消费金额: ¥${params[0].value.toLocaleString('zh-CN')}`
          }
        },
        grid: {
          left: '3%',
          right: '8%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          name: '营收 (¥)',
          axisLabel: {
            formatter: (v) => '¥' + (v >= 10000 ? (v/10000).toFixed(1) + 'w' : v)
          }
        },
        yAxis: {
          type: 'category',
          data: sortedE.map(item => item.name),
          axisLabel: {
            width: 100,
            overflow: 'truncate'
          }
        },
        series: [
          {
            name: '消费金额',
            type: 'bar',
            barMaxWidth: 30,
            data: sortedE.map(item => item.value),
            label: {
              show: true,
              position: 'right',
              formatter: (p) => '¥' + Number(p.value).toLocaleString('zh-CN')
            },
            itemStyle: {
              color: new echarts.graphic.LinearGradient(1, 0, 0, 0, [
                { offset: 0, color: '#83bff6' },
                { offset: 0.5, color: '#188df0' },
                { offset: 1, color: '#188df0' }
              ])
            }
          }
        ]
      }
      this.barChartInstance.setOption(option)
    },

    formatAmount(amount) {
      if (amount == null) return '0.00'
      return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped lang="scss">
.transaction-dashboard {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      color: #333;
    }
  }

  .stat-box {
    text-align: center;
    padding: 20px 0;
    
    .stat-title {
      font-size: 14px;
      color: #909399;
      margin-bottom: 10px;
    }
    
    .stat-value {
      font-size: 28px;
      font-weight: bold;
      color: #303133;
    }
  }
}

.positive-amount {
  color: #67C23A;
  font-weight: bold;
}
</style>